<template>
  <Navigation />
  <div class="auction-container">
    <div class="container mt-4">
      <h1 class="text-center mb-4">Fantasy Football Auction Results</h1>

      <!-- Filter Controls -->
      <div class="row mb-4">
        <div class="col-lg-10 mx-auto">
          <div class="card">
            <div class="card-header">
              <h5>Filter Auction Results</h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <label for="playerFilter" class="form-label"
                    >Player Name</label
                  >
                  <input
                    type="text"
                    class="form-control"
                    id="playerFilter"
                    v-model="filters.player"
                    placeholder="Search by player name..."
                  />
                </div>
                <div class="col-md-6">
                  <label for="teamFilter" class="form-label">Team Name</label>
                  <select
                    class="form-control"
                    id="teamFilter"
                    v-model="filters.team"
                  >
                    <option value="">All Teams</option>
                    <option
                      v-for="team in distinctTeams"
                      :key="team"
                      :value="team"
                    >
                      {{ team }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-12">
                  <button class="btn btn-secondary me-2" @click="clearFilters">
                    Clear Filters
                  </button>
                  <span class="text-muted"
                    >Showing {{ filteredAuctionData.length }} of
                    {{ auctionData.length }} players</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Auction Results -->
      <div class="row">
        <div class="col-lg-10 mx-auto">
          <div class="auction-results">
            <div
              v-for="(player, index) in filteredAuctionData"
              :key="player.PlayerName"
              class="player-card mb-2"
            >
              <div class="player-header">
                <button
                  class="player-button w-100 d-flex justify-content-between align-items-center"
                  type="button"
                  @click="togglePlayer(index)"
                >
                  <div class="player-summary">
                    <strong>{{ player.PlayerName }}</strong>
                    <span class="badge bg-success ms-2"
                      >${{ player.WinningBid }}</span
                    >
                    <span class="badge bg-primary ms-2">{{
                      player.WinningTeam
                    }}</span>
                    <span class="badge bg-info ms-2"
                      >{{ player.bids ? player.bids.length : 0 }} bids</span
                    >
                  </div>
                  <span
                    class="expand-icon"
                    :class="{ expanded: expandedPlayers[index] }"
                  >
                    ▼
                  </span>
                </button>
              </div>
              <div v-show="expandedPlayers[index]" class="player-details">
                <div class="player-body">
                  <div class="row">
                    <div class="col-md-6">
                      <h6>Auction Summary</h6>
                      <ul class="list-unstyled">
                        <li>
                          <strong>Final Price:</strong> ${{ player.WinningBid }}
                        </li>
                        <li>
                          <strong>Winning Team:</strong>
                          {{ player.WinningTeam }}
                        </li>
                        <li>
                          <strong>Time Remaining:</strong>
                          {{ player.TimeLeft }}s
                        </li>
                        <li>
                          <strong>Total Bids:</strong>
                          {{ player.bids ? player.bids.length : 0 }}
                        </li>
                      </ul>
                    </div>
                    <div
                      class="col-md-6"
                      v-if="player.bids && player.bids.length > 0"
                    >
                      <h6>Bidding Timeline for {{ player.PlayerName }}</h6>
                      <div class="bid-timeline">
                        <div
                          v-for="(bid, bidIndex) in player.bids.slice(0, 15)"
                          :key="bidIndex"
                          class="bid-item d-flex justify-content-between align-items-center mb-1"
                        >
                          <div class="bid-info">
                            <div class="bid-header">
                              <span class="bid-amount">${{ bid.amount }}</span>
                              <span class="bid-player">{{
                                player.PlayerName
                              }}</span>
                            </div>
                            <span class="bid-team">{{ bid.team }}</span>
                          </div>
                          <span class="time-left">{{ bid.clock }}s left</span>
                        </div>
                        <div v-if="player.bids.length > 15" class="text-muted">
                          ... and {{ player.bids.length - 15 }} more bids
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No results message -->
          <div v-if="filteredAuctionData.length === 0" class="text-center mt-5">
            <div class="card">
              <div class="card-body">
                <h5>No players found</h5>
                <p class="text-muted">
                  Try adjusting your filters to see more results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/Navigation.vue";
import auctionData from "@/assets/processed_auction_data.json";

export default {
  name: "Auction",
  components: {
    Navigation,
  },
  data() {
    return {
      auctionData: auctionData,
      expandedPlayers: {},
      filters: {
        player: "",
        team: "",
      },
    };
  },
  computed: {
    distinctTeams() {
      const teams = new Set();
      this.auctionData.forEach((player) => {
        teams.add(player.WinningTeam);
      });
      return Array.from(teams).sort();
    },
    filteredAuctionData() {
      return this.auctionData
        .filter((player) => {
          // Filter by player name
          if (
            this.filters.player &&
            !player.PlayerName.toLowerCase().includes(
              this.filters.player.toLowerCase()
            )
          ) {
            return false;
          }

          // Filter by team name
          if (
            this.filters.team &&
            this.filters.team !== "" &&
            player.WinningTeam !== this.filters.team
          ) {
            return false;
          }

          return true;
        })
        .sort((a, b) => b.WinningBid - a.WinningBid); // Sort by highest bid first
    },
  },
  methods: {
    clearFilters() {
      this.filters = {
        player: "",
        team: "",
      };
    },
    togglePlayer(index) {
      // Use Vue.set for Vue 2 compatibility or direct assignment for Vue 3
      if (this.$set) {
        // Vue 2
        this.$set(this.expandedPlayers, index, !this.expandedPlayers[index]);
      } else {
        // Vue 3 or fallback
        this.expandedPlayers[index] = !this.expandedPlayers[index];
        this.$forceUpdate();
      }
    },
  },
};
</script>

<style scoped>
.auction-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.card {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-header {
  background-color: #343a40;
  color: white;
  border-bottom: none;
}

/* Player Cards */
.player-card {
  border: none;
  border-radius: 0.375rem;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.player-header {
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.player-button {
  background: none;
  border: none;
  padding: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.player-button:hover {
  background-color: #f8f9fa;
}

.player-details {
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
}

.player-body {
  background-color: #f8f9fa;
  padding: 1rem;
}

.expand-icon {
  transition: transform 0.2s;
  font-size: 0.875rem;
  color: #6c757d;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.player-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.player-summary strong {
  color: #343a40;
  font-size: 1.1rem;
  font-weight: 600;
}

.badge {
  font-size: 0.75rem;
}

/* Bid Timeline */
.bid-timeline {
  max-height: 250px;
  overflow-y: auto;
  background-color: white;
  padding: 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid #dee2e6;
  /* Smooth scrolling for all devices */
  scroll-behavior: smooth;
  /* Better mobile scrolling */
  -webkit-overflow-scrolling: touch;
  /* Custom scrollbar for better mobile experience */
  scrollbar-width: thin;
  scrollbar-color: #6c757d #f8f9fa;
}

/* Custom scrollbar for webkit browsers */
.bid-timeline::-webkit-scrollbar {
  width: 8px;
}

.bid-timeline::-webkit-scrollbar-track {
  background: #f8f9fa;
  border-radius: 4px;
}

.bid-timeline::-webkit-scrollbar-thumb {
  background: #6c757d;
  border-radius: 4px;
}

.bid-timeline::-webkit-scrollbar-thumb:hover {
  background: #5a6268;
}

.bid-item {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  margin-bottom: 0.25rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.bid-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.bid-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
}

.bid-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.125rem;
  width: 100%;
}

.bid-amount {
  font-weight: 600;
  color: #198754;
  font-size: 1rem;
}

.bid-player {
  font-weight: 500;
  color: #0d6efd;
  font-size: 0.875rem;
  background-color: #e7f3ff;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}

.bid-team {
  font-size: 0.875rem;
  color: #495057;
  margin-top: 0.125rem;
  text-align: left;
}

.time-left {
  color: #6c757d;
  font-size: 0.875rem;
  font-weight: 500;
  margin-left: 1rem;
  white-space: nowrap;
}

.form-label {
  font-weight: 600;
  color: #495057;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-secondary:hover {
  background-color: #5a6268;
  border-color: #545b62;
}

/* Responsive design */
@media (max-width: 768px) {
  .player-summary {
    flex-direction: column;
    align-items: flex-start;
  }

  .player-summary .badge {
    display: none;
  }

  .badge {
    margin: 0.125rem 0;
  }

  .bid-timeline {
    max-height: 200px;
    /* Increase padding for easier touch scrolling */
    padding: 1rem;
    /* Make scrollbar more prominent on mobile */
    scrollbar-width: auto;
  }

  /* Make scrollbar more visible on mobile webkit browsers */
  .bid-timeline::-webkit-scrollbar {
    width: 12px;
  }

  .bid-timeline::-webkit-scrollbar-thumb {
    background: #495057;
    border-radius: 6px;
  }

  .bid-info {
    align-items: flex-start;
    flex: 1;
  }

  .bid-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
    width: 100%;
  }

  .bid-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .time-left {
    margin-top: 0.5rem;
    margin-left: 0;
    align-self: flex-start;
    text-align: left;
  }
}
</style>
